import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { getQuotas } from '@webapp/account/src/resources/profile/async';
import { addStateFetch } from '@webapp/account/src/resources/store-utils';

import { arrDeleteById, updateItemInArray } from '@webapp/common/lib/utils';

import { patchObject } from '../lib';

import {
    addExistingMailGroup,
    addMail,
    addMailGroup,
    addRecipient,
    addRecipientsFromCSV,
    changeMail,
    changeMailGroupName,
    changeMailParam,
    deleteMailGroup,
    emailStatDownloadCSV,
    getCommonEmailStat,
    getEmailDetails,
    getMailGroups,
    getMailList,
    getMailQuotasGetByDate,
    loadMailing,
    removeMailGroup,
    removeMailGroupFromMailing,
    removeRecipient,
    updateMailingGroupEmails,
} from './async';
import type { Mail, MailGroup } from './mailing';
import { initialMail, initialMailGroup } from './mailing';
import { initialState } from './state';

export const mailingSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        emailDetailsSetPage(s, { payload: { page } }: PayloadAction<{ page: number }>) {
            s.emailDetailsPage = page;
        },
        patchEmailDetailsFilters({ filters }, { payload: { change } }: PayloadAction<{ change: AnyObject }>) {
            patchObject(filters, change);
        },
        setSendDatetime(
            { item },
            { payload: { sendDatetime, sendNow } }: PayloadAction<{ sendDatetime: dayjs.Dayjs; sendNow: boolean }>,
        ) {
            patchObject(item, { sendNow, sendDatetime });
        },
    },
    extraReducers: (builder) =>
        addStateFetch(
            builder
                .addCase(getMailList.fulfilled, (s, { payload: { items } }) => {
                    s.items = items;
                    s.mailListLoaded = true;
                })
                .addCase(getQuotas.fulfilled, (s, { payload: { license } }) => {
                    s.todayEmailCount = license.emailLeft;
                })
                .addCase(
                    getEmailDetails.fulfilled,
                    (s, { payload: { count, emailDetails, filters, page, sendDates } }) => {
                        s.emailDetails = emailDetails;
                        s.emailDetailsPage = page;
                        s.filters = filters;
                        s.emailDetailsTotalCount = count;
                        s.sendDates = sendDates;
                    },
                )
                .addCase(changeMailParam.fulfilled, ({ item }, { payload: { params, duplicate } }) => {
                    item.params = params;
                    item.duplicate = duplicate;
                })
                .addCase(addMail.fulfilled, (s, { payload: { mail } }) => {
                    s.items.unshift(Object.assign(cloneDeep(initialMail), mail));
                })
                .addCase(addExistingMailGroup.fulfilled, ({ item, mailGroups }, { payload: { groupId } }) => {
                    const newMailGroup = mailGroups.find(({ id }) => id === groupId);

                    if (newMailGroup) {
                        item.mailGroups.push(newMailGroup);
                    }
                })
                .addCase(
                    updateMailingGroupEmails.fulfilled,
                    ({ item, mailGroups }, { payload: { emails, groupId } }) => {
                        updateItemInArray(item.mailGroups, groupId, (g) => {
                            g.emails = emails;
                        });
                        updateItemInArray(mailGroups, groupId, (g) => {
                            g.emails = emails;
                        });
                    },
                )
                .addCase(changeMailGroupName.fulfilled, ({ item, mailGroups }, { payload: { groupId, name } }) => {
                    updateItemInArray(mailGroups, groupId, (g) => {
                        g.name = name;
                    });
                    updateItemInArray(item.mailGroups, groupId, (g) => {
                        g.name = name;
                    });
                })
                .addCase(emailStatDownloadCSV.pending, (s) => {
                    s.csvDownloadFetch = true;
                })
                .addCase(emailStatDownloadCSV.rejected, (s) => {
                    s.csvDownloadFetch = false;
                })
                .addCase(emailStatDownloadCSV.fulfilled, (s, { payload: { file } }) => {
                    s.csvDownloadFetch = false;
                    window.open(file, 'blank');
                })
                .addCase(deleteMailGroup.fulfilled, ({ item, mailGroups }, { payload: { groups } }) => {
                    arrDeleteById(mailGroups, groups);
                    arrDeleteById(item.mailGroups, groups);
                })
                .addCase(getCommonEmailStat.fulfilled, (s, { payload: { sendDates, stat } }) => {
                    s.stat = stat;
                    s.sendDates = sendDates;
                })
                .addCase(
                    addMailGroup.fulfilled,
                    ({ item, mailGroups }, { payload: { emails, hidden, name, newGroupId } }) => {
                        const group: MailGroup = {
                            ...cloneDeep(initialMailGroup),
                            id: newGroupId,
                            hidden,
                            name,
                            emails,
                        };

                        item.mailGroups.push(group);
                        mailGroups.push(group);
                    },
                )
                .addCase(removeMailGroup.fulfilled, ({ item, mailGroups }, { payload: { groups } }) => {
                    arrDeleteById(item.mailGroups, groups);
                    arrDeleteById(mailGroups, groups);
                })
                .addCase(addRecipient.fulfilled, (s, { payload: { email, id } }) => {
                    s.item.recipients.unshift({
                        email,
                        id,
                    });
                })
                .addCase(removeRecipient.fulfilled, ({ item }, { payload: { ids } }) => {
                    ids = new Set(ids); // TODO wtf
                    item.recipients = item.recipients.filter(({ id }) => !ids.has(id));
                })
                .addCase(getMailQuotasGetByDate.fulfilled, (s, { payload: { license } }) => {
                    s.todayEmailCount = license.emailLeft; // TODO ?? only emailsLeft
                    s.quotasFetch = false;
                })
                .addCase(getMailGroups.fulfilled, (s, { payload: { mailGroups } }) => {
                    s.mailGroups = mailGroups;
                })
                .addCase(addRecipientsFromCSV.pending, (s) => {
                    s.fileUpload = true;
                })
                .addCase(addRecipientsFromCSV.rejected, (s) => {
                    s.fileUpload = false;
                })
                .addCase(addRecipientsFromCSV.fulfilled, (s, { payload: { group } }) => {
                    s.item.mailGroups.push(group);
                    s.fileUpload = false;
                })
                .addCase(loadMailing.pending, ({ item }) => {
                    item.fetch = true;
                })
                .addCase(loadMailing.fulfilled, (s, { payload: { item } }) => {
                    s.item = item;
                    s.item.fetch = false;
                })
                .addCase(removeMailGroupFromMailing.fulfilled, ({ item: { mailGroups } }, { payload: { groups } }) => {
                    arrDeleteById(mailGroups, groups);
                })
                .addCase(changeMail.fulfilled, ({ item, items }, { payload: { change, id } }) => {
                    Object.assign(item, change);
                    updateItemInArray(items, id, (m: Mail) => {
                        Object.assign(m, change);
                    });
                }),
        ),
});
